import { Icon } from "@iconify/react";
import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Badge, Row, Col, Card } from "react-bootstrap";
import apiTokensService from "../../services/api/apiTokens";
import SuccessModal from "../common/SuccessModal";
import ConfirmationModal from "../common/ConfirmationModal";
import styles from './ApiLayer.module.scss';

const ApiLayer = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [error, setError] = useState(null);
  
  // Formulário de criação
  const [description, setDescription] = useState("");
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(60);
  const [rateLimitPerHour, setRateLimitPerHour] = useState(1000);
  const [ipWhitelist, setIpWhitelist] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  // Carregar tokens ao montar componente
  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiTokensService.getTokens();
      if (response.success) {
        setTokens(response.data || []);
      }
    } catch (err) {
      console.error('❌ Erro ao carregar tokens:', err);
      setError('Erro ao carregar tokens. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!description.trim()) {
      setError('Descrição é obrigatória');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = {
        description: description.trim(),
        rate_limit_per_minute: rateLimitPerMinute,
        rate_limit_per_hour: rateLimitPerHour
      };

      // Adicionar IP whitelist se preenchido
      if (ipWhitelist.trim()) {
        data.ip_whitelist = ipWhitelist.split(',').map(ip => ip.trim()).filter(ip => ip);
      }

      // Adicionar data de expiração se preenchida
      if (expiresAt) {
        data.expires_at = new Date(expiresAt).toISOString();
      }

      const response = await apiTokensService.createToken(data);
      
      if (response.success) {
        setNewToken(response.data.token);
        setShowCreateModal(false);
        setShowTokenModal(true);
        // Limpar formulário
        setDescription("");
        setRateLimitPerMinute(60);
        setRateLimitPerHour(1000);
        setIpWhitelist("");
        setExpiresAt("");
        // Recarregar lista
        await loadTokens();
      }
    } catch (err) {
      console.error('❌ Erro ao criar token:', err);
      setError(err.response?.data?.error?.message || 'Erro ao criar token');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!selectedToken) return;

    try {
      setLoading(true);
      await apiTokensService.revokeToken(selectedToken.id);
      setShowRevokeModal(false);
      setSelectedToken(null);
      await loadTokens();
    } catch (err) {
      console.error('❌ Erro ao revogar token:', err);
      setError(err.response?.data?.error?.message || 'Erro ao revogar token');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  const getStatusBadge = (token) => {
    if (!token.is_active) {
      return <Badge bg="danger">Revogado</Badge>;
    }
    if (token.is_expired) {
      return <Badge bg="warning">Expirado</Badge>;
    }
    return <Badge bg="success">Ativo</Badge>;
  };

  return (
    <div className={styles.apiLayer}>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
              {error}
            </Alert>
          )}

          {/* Header com Botão */}
          <div className={styles.apiLayer__header}>
            <span className={styles.apiLayer__title}>
              <Icon icon="mdi:key-variant" />
              Tokens de API ({tokens.length})
            </span>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Icon icon="mdi:plus" style={{ fontSize: '20px' }} />
              Novo Token
            </Button>
          </div>

          {/* Lista de Tokens */}
          {loading && tokens.length === 0 ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingState__spinner}>⏳</div>
              <span className={styles.loadingState__text}>Carregando tokens...</span>
            </div>
          ) : tokens.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyState__icon}>🔑</span>
              <span className={styles.emptyState__text}>Nenhum token encontrado. Crie seu primeiro token para começar a usar a API.</span>
            </div>
          ) : (
            <div className={styles.tokensList}>
              {tokens.map((token) => {
                const firstLetter = token.description?.charAt(0)?.toUpperCase() || 'T';
                return (
                  <div key={token.id} className={styles.tokenCard}>
                    <div className={styles.tokenCard__avatar}>
                      <span className={styles.tokenCard__avatarText}>{firstLetter}</span>
                    </div>
                    
                    <div className={styles.tokenCard__info}>
                      <div className={styles.tokenCard__name}>
                        {token.description || 'Token sem descrição'}
                        {getStatusBadge(token)}
                      </div>
                      <div className={styles.tokenCard__description}>
                        {formatDate(token.created_at) ? `Criado em ${formatDate(token.created_at)}` : 'Data de criação não disponível'}
                        {token.last_used_at && formatDate(token.last_used_at) && ` • Último uso: ${formatDate(token.last_used_at)}`}
                      </div>
                    </div>

                    <div className={styles.tokenCard__stats}>
                      <div className={styles.tokenCard__stat}>
                        <span className={styles.tokenCard__statValue}>
                          {token.rate_limit_per_minute || 60}
                        </span>
                        <span className={styles.tokenCard__statLabel}>Req/min</span>
                      </div>
                      <div className={styles.tokenCard__stat}>
                        <span className={styles.tokenCard__statValue}>
                          {token.rate_limit_per_hour || 1000}
                        </span>
                        <span className={styles.tokenCard__statLabel}>Req/hora</span>
                      </div>
                      {token.expires_at && (
                        <div className={styles.tokenCard__stat}>
                          <span className={styles.tokenCard__statValue} style={{ fontSize: '12px' }}>
                            {new Date(token.expires_at) > new Date() ? 'Válido' : 'Expirado'}
                          </span>
                          <span className={styles.tokenCard__statLabel}>Expiração</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.tokenCard__actions}>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setSelectedToken(token);
                          setShowRevokeModal(true);
                        }}
                        disabled={!token.is_active}
                        title="Revogar token"
                      >
                        <Icon icon="mdi:delete" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Criar Token */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)} 
        centered 
        size="lg"
        className={styles.apiTokenModal}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <Icon 
              icon="mdi:key-plus" 
              className={styles.titleIcon} 
            />
            Novo token de API
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <p className={styles.modalSubtitle}>
            Um token permite que sistemas externos acessem sua conta via API. Use tokens para integrações com ERPs, e-commerces e automações.
          </p>

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:text" /> Descrição <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Token para integração Bling"
                required
                className={styles.input}
              />
              <Form.Text className="text-muted">
                Descreva onde e como este token será usado
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className={styles.label}>
                    <Icon icon="mdi:timer" /> Limite por minuto
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={rateLimitPerMinute}
                    onChange={(e) => setRateLimitPerMinute(parseInt(e.target.value) || 60)}
                    min="1"
                    max="1000"
                    className={styles.input}
                  />
                  <Form.Text className="text-muted">Padrão: 60 requisições/minuto</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className={styles.label}>
                    <Icon icon="mdi:clock-outline" /> Limite por hora
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={rateLimitPerHour}
                    onChange={(e) => setRateLimitPerHour(parseInt(e.target.value) || 1000)}
                    min="1"
                    max="10000"
                    className={styles.input}
                  />
                  <Form.Text className="text-muted">Padrão: 1000 requisições/hora</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:ip-network" /> IPs permitidos (opcional)
              </Form.Label>
              <Form.Control
                type="text"
                value={ipWhitelist}
                onChange={(e) => setIpWhitelist(e.target.value)}
                placeholder="Ex: 192.168.1.100, 10.0.0.50"
                className={styles.input}
              />
              <Form.Text className="text-muted">
                Separe múltiplos IPs por vírgula. Deixe em branco para permitir qualquer IP.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:calendar-clock" /> Data de expiração (opcional)
              </Form.Label>
              <Form.Control
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className={styles.input}
              />
              <Form.Text className="text-muted">
                Deixe em branco para token que nunca expira.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button 
            variant="secondary" 
            onClick={() => setShowCreateModal(false)} 
            disabled={loading}
            className={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateToken} 
            disabled={loading || !description.trim()}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                Criando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="me-1" />
                Criar token
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Token Criado */}
      <Modal show={showTokenModal} onHide={() => setShowTokenModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <Icon icon="mdi:check-circle" className="me-2" />
            Token criado com sucesso!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-4">
            <Icon icon="mdi:alert" className="me-2" />
            <strong>⚠️ ATENÇÃO:</strong> Este token será exibido apenas uma vez. Guarde-o em local seguro!
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Seu token de API:</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={newToken || ''}
                readOnly
                className="font-monospace"
                style={{ fontSize: '0.9rem' }}
              />
              <Button
                variant="outline-primary"
                onClick={() => {
                  navigator.clipboard.writeText(newToken);
                }}
              >
                <Icon icon="mdi:content-copy" />
              </Button>
            </div>
          </Form.Group>

          <Alert variant="info">
            <strong>Como usar:</strong>
            <br />
            Adicione este token no header <code>Authorization: Bearer {newToken?.substring(0, 20)}...</code>
            <br />
            <small>Documentação completa: <a href="/api-docs-public" target="_blank">/api-docs-public</a></small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTokenModal(false)}>
            <Icon icon="mdi:check" className="me-2" />
            Entendi, guardei o token
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Revogação */}
      <ConfirmationModal
        show={showRevokeModal}
        onHide={() => {
          setShowRevokeModal(false);
          setSelectedToken(null);
        }}
        onConfirm={handleRevokeToken}
        title="Revogar token"
        message={`Tem certeza que deseja revogar o token "${selectedToken?.description}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, revogar"
        cancelText="Cancelar"
        variant="danger"
        icon="mdi:alert-circle"
        isLoading={loading}
      />
    </div>
  );
};

export default ApiLayer;
